import React from 'react';
import { CollapseFilterWrapper } from '../../../components/sidebar-filters/CollapseFilterWrapper';
import { AlertTrackEventStatusSelect } from '../../../components/sidebar-filters/filter-select/AlertTrackEventStatusSelect';
import { AlertTrackOpenedFilterSelect } from '../../../components/sidebar-filters/filter-select/AlertTrackOpenedFilterSelect';
import { AlertTrackUserSelect } from '../../../components/sidebar-filters/filter-select/AlertTrackUserSelect';
import { DateRangeWithCustomOptionFilterSelect } from '../../../components/sidebar-filters/filter-select/DateRangeWithCustomOptionFilterSelect';
import { ProgramFilterSelect } from '../../../components/sidebar-filters/filter-select/ProgramFilterSelect';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { IEventTrackingSidebarFilters, useEventTrackingSidebarFiltersContext } from './EventTrackingSidebarFiltersContext';
import { AlertTrackStatusSelect } from '../../../components/sidebar-filters/filter-select/AlertTrackStatusSelect';
import { AlertTrackParticipationStatusSelect } from '../../../components/sidebar-filters/filter-select/AlertTrackResponseStatusFilterSelect';

export const EventTrackingFilterKeys = {
    // Filters
    period: 'period',
    user_ids: 'user_ids',
    alert_statuses: 'alert_statuses',
    event_statuses: 'event_statuses',
    event_programs: 'event_programs',
    is_opened: 'is_opened',
    participation_statuses: 'participation_statuses',
} as const;

export type TEventTrackingFilterKey = (typeof EventTrackingFilterKeys)[keyof typeof EventTrackingFilterKeys];

export const EventTrackingFilterLabels = {
    [EventTrackingFilterKeys.period]: 'Period',
    [EventTrackingFilterKeys.user_ids]: 'User',
    [EventTrackingFilterKeys.alert_statuses]: 'Notification Type',
    [EventTrackingFilterKeys.event_statuses]: 'Event Status',
    [EventTrackingFilterKeys.event_programs]: 'Program',
    [EventTrackingFilterKeys.is_opened]: 'Opened',
    [EventTrackingFilterKeys.participation_statuses]: 'Participation Status',
} as const;

interface IEventTrackingSidebarFiltersProps {
    onFilterChange: (filters: IEventTrackingSidebarFilters) => void;
}

export const EventTrackingSidebarFilters: React.FC<IEventTrackingSidebarFiltersProps> = ({ onFilterChange }) => {
    const { filters, handleFilterChange } = useEventTrackingSidebarFiltersContext();
    const {
        isLoading: isFiltersLoading,
        alertTrackingProgramOptions,
        alertTrackingPeriodOptions,
        alertTrackingUserOptions,
        alertTrackStatusOptions,
        eventStatusOptions,
        alertTrackOpenedOptions,
        alertTrackParticipationStatusOptions,
    } = useSidebarFilterOptionsContext();

    const handleEventTrackingFilterChange = (key: string, value: unknown) => {
        handleFilterChange(key, value);
        onFilterChange({ ...filters, [key]: value });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            {/* Filters */}
            <CollapseFilterWrapper label="Filters">
                <DateRangeWithCustomOptionFilterSelect
                    height={180}
                    label={EventTrackingFilterLabels[EventTrackingFilterKeys.period]}
                    value={filters[EventTrackingFilterKeys.period] || null}
                    onChange={value => handleEventTrackingFilterChange(EventTrackingFilterKeys.period, value)}
                    options={alertTrackingPeriodOptions}
                    allowClear={false}
                    tooltipTitle="Period must be selected, due to large amount of data"
                    maxRangeInDays={90}
                />
                <AlertTrackStatusSelect
                    label={EventTrackingFilterLabels[EventTrackingFilterKeys.alert_statuses]}
                    value={filters[EventTrackingFilterKeys.alert_statuses] || []}
                    onChange={value => handleEventTrackingFilterChange(EventTrackingFilterKeys.alert_statuses, value)}
                    options={alertTrackStatusOptions}
                />
                <ProgramFilterSelect
                    label={EventTrackingFilterLabels[EventTrackingFilterKeys.event_programs]}
                    value={filters[EventTrackingFilterKeys.event_programs] || []}
                    onChange={value => handleEventTrackingFilterChange(EventTrackingFilterKeys.event_programs, value)}
                    options={alertTrackingProgramOptions}
                    isLoading={isFiltersLoading}
                />
                <AlertTrackEventStatusSelect
                    label={EventTrackingFilterLabels[EventTrackingFilterKeys.event_statuses]}
                    value={filters[EventTrackingFilterKeys.event_statuses] || []}
                    onChange={value => handleEventTrackingFilterChange(EventTrackingFilterKeys.event_statuses, value)}
                    options={eventStatusOptions}
                />
                <AlertTrackUserSelect
                    label={EventTrackingFilterLabels[EventTrackingFilterKeys.user_ids]}
                    value={filters[EventTrackingFilterKeys.user_ids] || []}
                    onChange={value => handleEventTrackingFilterChange(EventTrackingFilterKeys.user_ids, value)}
                    options={alertTrackingUserOptions}
                    isLoading={isFiltersLoading}
                />
                <AlertTrackOpenedFilterSelect
                    label={EventTrackingFilterLabels[EventTrackingFilterKeys.is_opened]}
                    value={filters[EventTrackingFilterKeys.is_opened] || null}
                    onChange={val => handleEventTrackingFilterChange(EventTrackingFilterKeys.is_opened, val)}
                    options={alertTrackOpenedOptions}
                />
                <AlertTrackParticipationStatusSelect
                    label={EventTrackingFilterLabels[EventTrackingFilterKeys.participation_statuses]}
                    value={filters[EventTrackingFilterKeys.participation_statuses] || []}
                    onChange={val => handleEventTrackingFilterChange(EventTrackingFilterKeys.participation_statuses, val)}
                    options={alertTrackParticipationStatusOptions}
                />
            </CollapseFilterWrapper>
        </div>
    );
};
